import React, { useState, useRef, useEffect } from 'react';
import { getOrderId, isMobile } from '@/biz/tool';
import toast from 'react-hot-toast';
import WebModal from './Modal';
import BottomDrawer from './BottomDrawer';
import { Elements, AddressElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import appConfig from '@/utils/config';
import { NationsLab } from '@/utils/locations';
import { useLoginStore } from '@/hooks/useLoginStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import axiosInstance from '@/apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import { getUserLang } from '@/i18n/utils';
import { TriangleExclamationIcon, WarnRedIcon } from '@/components/common/icon';
import getHoursDiffFromNow from '@/utils/getHoursDiffFromNow';
import { Input } from 'aha-components';

import styles from './ModalAddress.module.less';

// 获取当前语言并映射到 Stripe 支持的语言代码
const getCurrentLocale = () => {
  const lang = getUserLang().detectedValue;
  const localeMap = {
    'en-US': 'en',
    'pt-BR': 'pt-BR',
    'ja-JP': 'ja'
  } as const;
  return localeMap[lang as keyof typeof localeMap] || 'en';
};

const stripePromise = loadStripe(appConfig['stripe_public_key'], {
  locale: getCurrentLocale()
});

// RFC 完整验证函数
const validateRFC = (value: string): boolean => {
  // 1. 长度验证
  if (value.length !== 13) {
    return false;
  }

  // 2. 前4位：仅字母（A-Z + 带重音的西班牙语大写字母）
  const first4 = value.substring(0, 4);
  // A-Z (U+0041-U+005A) + Latin-1 Supplement (U+00C0-U+00FF) 包含带重音的字母
  if (!/^[A-Z\u00C0-\u00FF]{4}$/.test(first4)) {
    return false;
  }

  // 3. 中间6位：日期 YYMMDD
  const dateStr = value.substring(4, 10);
  if (!/^\d{6}$/.test(dateStr)) {
    return false;
  }

  // 解析日期
  const yy = parseInt(dateStr.substring(0, 2));
  const mm = parseInt(dateStr.substring(2, 4));
  const dd = parseInt(dateStr.substring(4, 6));

  // 验证年月日范围
  if (mm < 1 || mm > 12) {
    return false;
  }

  // 年份转换和验证
  // RFC 年份解读：
  // - YY 50-99 -> 1950-1999
  // - YY 00-当前年份后两位 -> 2000-当前年
  const currentYear = new Date().getFullYear();
  const currentYearLastTwo = currentYear % 100;
  let fullYear;

  if (yy >= 50 && yy <= 99) {
    fullYear = 1900 + yy; // 1950-1999
  } else if (yy >= 0 && yy <= currentYearLastTwo) {
    fullYear = 2000 + yy; // 2000-当前年
  } else {
    return false; // 年份超出范围
  }

  // RFC 最早日期：1950-01-01
  if (fullYear < 1950) {
    return false;
  }

  // 验证日期是否有效
  const daysInMonth = new Date(fullYear, mm, 0).getDate();
  if (dd < 1 || dd > daysInMonth) {
    return false;
  }

  // 验证日期不能超过今天
  const date = new Date(fullYear, mm - 1, dd);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) {
    return false;
  }

  // 4. 后3位：字母或数字
  const last3 = value.substring(10, 13);
  if (!/^[A-Z0-9]{3}$/.test(last3)) {
    return false;
  }

  return true;
};

// Prevent PO Box / PMB / Post Office Box in shipping address
const containsProhibitedPOBox = (input: string): boolean => {
  const text = input || '';
  // Matches: P.O. Box / POBOX / P O Box / P.O. B.O.X (spaces and punctuation tolerant)
  const poBoxRegex = /P\s*\.?\s*O\s*\.?\s*B\s*\.?\s*O\s*\.?\s*X/i;
  // Matches: Post Office Box / Post.Office.Box (full phrase, spaces and punctuation tolerant)
  const postOfficeBoxRegex = /POST[\s.]*OFFICE[\s.]*BOX/i;
  // Matches: PMB / P.M.B / P M B (spaces and punctuation tolerant)
  const pmbRegex = /P\s*\.?\s*M\s*\.?\s*B/i;

  return (
    poBoxRegex.test(text) ||
    postOfficeBoxRegex.test(text) ||
    pmbRegex.test(text)
  );
};

interface Props {}

const ModalAddress: React.FC<Props> = ({}) => {
  const { t } = useTranslation();
  const { detailData, reQueryDetailData } = useDetailDataStore();
  const { isShowAddressModal, setShowAddressModal } = useWebModalStore();
  const { setNowSuccessNode, setShowModalBonusNext, setShowModalNodeSuccess } =
    useWebModalStore();
  const [complete, setComplete] = useState(false);
  const [stripeComplete, setStripeComplete] = useState(false);
  const [poBoxInvalid, setPoBoxInvalid] = useState(false);
  const [address, setAddress] = useState<any>(null);
  const orderId = getOrderId();
  const [stepNodes, setStepNodes] = useState(1); // 0: 不需要节点，1: 填写信息  2：填写地址
  const { product, order } = detailData;
  const [skuListData, setSkuListData] = useState<any[]>(
    (order?.skuList?.length > 0 ? order?.skuList : product?.skuList) || []
  );
  useEffect(() => {
    const currentSkuList =
      (order?.skuList?.length > 0 ? order?.skuList : product?.skuList) || [];
    setSkuListData(currentSkuList);
    if (order?.skuList?.length > 0) {
      setStepNodes(2);
    } else if (product?.skuList?.length > 0) {
      setStepNodes(1);
    } else {
      setStepNodes(0);
    }
  }, [order?.skuList, product?.skuList]);

  const handleSubmit = async () => {
    try {
      const addressText = `${(address as any)?.line1 || ''} ${(address as any)?.line2 || ''}`;
      if (containsProhibitedPOBox(addressText)) {
        toast.error(
          t('modal.address.no_po_box.error', {
            defaultValue:
              'Please provide a physical shipping address (no PO Boxes).'
          })
        );
        return;
      }
      await axiosInstance.post(`/order/${orderId}/address`, {
        addressInfo: address
      });
      reQueryDetailData({ orderId });
      setShowAddressModal(false);
      // 此处区分有无bonus，弹不同提示弹窗
      // 填写地址逾期时，要等提交成功后，才会清零奖金，所以特殊处理下，判断有没有逾期
      // 但是这个又有一种情况是，可以申诉，申诉成功后，可以继续获得奖金 所以新增判断当前阶段是否申诉成功过
      const delayTime = getHoursDiffFromNow(
        detailData?.order?.creatorNextOperateDate
      );
      setNowSuccessNode(detailData.order?.displayStatus);
      if (
        (delayTime > 0 || detailData.order?.isCurrentStageHasApprovedAppeal) &&
        detailData?.order?.interestedPriceFields?.[0]?.bonusAmount > 0
      ) {
        setShowModalBonusNext(true);
      } else {
        setShowModalNodeSuccess(true);
      }
    } catch (error) {
      toast.error(
        t('modal.address.submit.error', { defaultValue: 'Submission failed.' })
      );
    }
  };

  // locations
  const handleAddressChange = (event: any) => {
    const { complete, value, rfcValue } = event;
    const locations: any[] = detailData?.campaign?.locations || [];

    const { address, name, phone } = value;
    setAddress({
      ...address,
      name,
      phone,
      rfcValue: address?.country === 'MX' ? rfcValue : ''
    });
    setStripeComplete(complete);

    const addressText = `${(address as any)?.line1 || ''} ${(address as any)?.line2 || ''}`;
    const hasProhibitedPOBox = containsProhibitedPOBox(addressText);
    if (hasProhibitedPOBox) {
      // 每次检测到禁止内容都提示，避免从一种禁止写法改成另一种时用户看不到报错
      toast.error(
        t('modal.address.no_po_box.error', {
          defaultValue:
            'Please provide a physical shipping address (no PO Boxes).'
        })
      );
      setPoBoxInvalid(true);
      setComplete(false);
      return;
    }
    if (poBoxInvalid) {
      setPoBoxInvalid(false);
    }

    const isInclude = locations?.some((item: string[]) =>
      item?.includes(value?.address?.country)
    );
    if (!isInclude) {
      toast.error(
        t('modal.address.location.error', {
          defaultValue:
            "We're sorry that your location is not meeting the brand's requirements. To begin this collaboration, please ensure that you are located in one of the following countries: {{countries}}.",
          countries: locations
            ?.map((item) => NationsLab?.[item?.[1]])
            .join(', ')
        })
      );
      setComplete(false);
      return;
    }

    if (address?.country === 'MX' && !validateRFC(rfcValue)) {
      setComplete(false);
      return;
    }
    setComplete(complete);
  };

  return (
    <>
      {isMobile() ? (
        <BottomDrawer
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          submitDisabled={!complete}
          isShowHeader={false}
          isShowFooter={stepNodes !== 1}
          isShowBack={stepNodes !== 0 ? true : false}
          handleBack={() => setStepNodes(1)}
          okText={t('modal.address.confirm', { defaultValue: 'Submit' })}
        >
          {stepNodes !== 0 && (
            <TopStep stepNodes={stepNodes} setStepNodes={setStepNodes} />
          )}
          {stepNodes === 1 && (
            <SelectProductContent
              stepNodes={stepNodes}
              setStepNodes={setStepNodes}
              skuListData={skuListData}
              setSkuListData={setSkuListData}
              setShowAddressModal={setShowAddressModal}
            />
          )}
          {(stepNodes === 0 || stepNodes === 2) && (
            <ModalContent
              handleAddressChange={handleAddressChange}
              addressInfo={address}
              complete={stripeComplete}
            />
          )}
        </BottomDrawer>
      ) : (
        <WebModal
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          submitDisabled={!complete}
          isShowHeader={false}
          isShowFooter={stepNodes !== 1}
          okText={t('modal.address.confirm', { defaultValue: 'Submit' })}
          isShowBack={stepNodes !== 0 ? true : false}
          handleBack={() => setStepNodes(1)}
        >
          {stepNodes !== 0 && (
            <TopStep stepNodes={stepNodes} setStepNodes={setStepNodes} />
          )}
          {stepNodes === 1 && (
            <SelectProductContent
              stepNodes={stepNodes}
              setStepNodes={setStepNodes}
              skuListData={skuListData}
              setSkuListData={setSkuListData}
              setShowAddressModal={setShowAddressModal}
            />
          )}
          {(stepNodes === 0 || stepNodes === 2) && (
            <ModalContent
              handleAddressChange={handleAddressChange}
              addressInfo={address}
              complete={stripeComplete}
            />
          )}
        </WebModal>
      )}
    </>
  );
};

const TopStep: React.FC<{
  stepNodes: number;
  setStepNodes: (step: number) => void;
}> = ({ stepNodes, setStepNodes }) => {
  const { t } = useTranslation();
  return (
    <div className="w-full p-3 bg-[#FAFAF9] rounded-lg flex justify-center items-center mb-4 relative">
      <div className="flex flex-col flex-1 justify-center items-center gap-2">
        <div
          className={`w-6 h-6 rounded-full text-center border text-xs font-medium leading-[22px] ${stepNodes != 1 ? 'border-[#079455] text-[#fff] bg-[#079455] cursor-pointer' : 'bg-[#fff] border-[#E7E5E4]'}`}
          onClick={() => setStepNodes(1)}
        >
          {stepNodes === 1 ? '1' : '✓'}
        </div>
        <div className="text-sm font-medium leading-5">
          {t('modal.address.select_product_title', {
            defaultValue: 'Select your product'
          })}
        </div>
      </div>
      <div className="flex flex-col flex-1 justify-center items-center gap-2">
        <div className="w-6 h-6 bg-[#fff] rounded-full text-center border border-[#E7E5E4] text-xs font-medium leading-[22px]">
          2
        </div>
        <div className="text-sm font-medium leading-5">
          {t('modal.address.submit_address', {
            defaultValue: 'Submit address'
          })}
        </div>
      </div>
      <div
        className="absolute w-[50%] h-1 inset-0 flex"
        style={{
          padding: '0 20px',
          left: '25%',
          top: '22px',
          gap: `${6}px`,
          transform: 'translateX(0)'
        }}
      >
        {Array.from({ length: Math.ceil(100 / 6) }).map((_, i) => (
          <div
            key={i}
            className="w-[3px] h-[3px] bg-gray-300 rounded-full"
            style={{ margin: '0 auto' }}
          />
        ))}
      </div>
    </div>
  );
};

const SelectProductContent: React.FC<{
  stepNodes: number;
  setShowAddressModal: (show: boolean) => void;
  setStepNodes: (step: number) => void;
  skuListData: any[];
  setSkuListData: (skuListData: any[]) => void;
}> = ({
  stepNodes,
  setStepNodes,
  setShowAddressModal,
  skuListData = [],
  setSkuListData
}) => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const { product, order } = detailData;
  const { setShowModalShowImage } = useWebModalStore();

  const handleContinue = async () => {
    const res = await axiosInstance.post(`/order/${order?.id}/productInfo`, {
      skuList: skuListData
    });
    if (res.data.code === 0) {
      setStepNodes(2);
    } else {
      toast.error(res.data.message);
    }
  };

  return (
    <div className={isMobile() ? styles.mobileContent : styles.content}>
      <div className="text-base font-semibold leading-6 mb-3">
        {t('modal.address.select_product_title', {
          defaultValue: 'Select your product'
        })}
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-[#525252] text-sm font-normal leading-5">
          {t('modal.address.select_product_description', {
            defaultValue:
              'Please select the product options you want to receive. Your selection will be used to ship the product.'
          })}
        </span>
        <div className="self-stretch p-2 bg-[#FFF6ED] rounded-lg border border-[#FB6011] inline-flex justify-start items-center gap-2">
          <img
            className="w-11 h-11 object-contain rounded-lg border border-zinc-100 bg-[#fff]"
            src={product?.physicalProductPhoto}
          />
          <div className="flex-1 justify-start text-font&icon-primary text-sm font-normal leading-5">
            {' '}
            {product?.physicalProductName}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 mt-4">
        {skuListData?.map((item: any, index: number) => (
          <div key={index} className="flex flex-col gap-2">
            <div>
              {item.label}
              <span className="text-red-500"> *</span>
            </div>
            <div className="text-[#A6A09B] text-sm font-normal leading-5">
              {item.description}
            </div>
            <Input
              placeholder={t('modal.address.enter_option', {
                defaultValue: 'Enter your option'
              })}
              value={item?.value}
              onChange={(e) =>
                setSkuListData(
                  skuListData?.map((item: any, idx: number) =>
                    idx === index ? { ...item, value: e.target.value } : item
                  )
                )
              }
            />
            <div className="flex gap-2">
              {item.images &&
                item.images.map((image: any, index: number) => (
                  <img
                    key={index}
                    src={image.src}
                    className="w-11 h-11 rounded-lg cursor-pointer"
                    onClick={() => setShowModalShowImage(true, image.src)}
                  />
                ))}
            </div>
          </div>
        ))}
      </div>
      <div
        className={`flex ${isMobile() ? 'flex-col-reverse' : 'justify-end'} items-center gap-2 mt-4`}
      >
        <div
          className={`${isMobile() ? 'w-full text-center' : ''} px-4 py-2 bg-white rounded-lg border border-zinc-200 text-sm font-medium leading-5 cursor-pointer ${isMobile() ? 'w-full' : ''}`}
          onClick={() => setShowAddressModal(false)}
        >
          {t('modal.address.cancel', { defaultValue: 'Cancel' })}
        </div>
        <div
          className={`bg-theme-color text-[#fff] px-4 py-2 rounded-lg text-sm font-medium leading-5 ${skuListData?.some((item: any) => !item.value) ? 'opacity-50' : 'cursor-pointer'} ${isMobile() ? 'w-full text-center' : ''}`}
          onClick={() => handleContinue()}
        >
          {t('modal.address.continue', { defaultValue: 'Continue' })}
        </div>
      </div>
    </div>
  );
};

const ModalContent: React.FC<{
  handleAddressChange: any;
  addressInfo: any;
  complete: boolean;
}> = ({ handleAddressChange, addressInfo, complete }) => {
  const { t } = useTranslation();
  const { userAddressInfo } = useLoginStore();
  const { name, phone, ...address } = userAddressInfo || {};
  const { detailData } = useDetailDataStore();
  const { setEducationModal } = useWebModalStore();
  const [rfcValue, setRfcValue] = useState(address?.rfcValue || '');
  const [showRfcError, setShowRfcError] = useState(false);
  const rfcInputRef = useRef<HTMLInputElement>(null);

  // 值变化校验
  const handleRfcChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const oldCursorPosition = input.selectionStart || 0;

    // 移除所有空格，保持光标位置
    const newValue = input.value.toUpperCase().replace(/\s/g, '');

    // 计算移除的空格数量（光标之前的空格）
    const spacesBeforeCursor = input.value
      .substring(0, oldCursorPosition)
      .replace(/\S/g, '').length;
    const newCursorPosition = Math.max(
      0,
      oldCursorPosition - spacesBeforeCursor
    );

    setRfcValue(newValue);
    const info = addressInfo ? addressInfo : userAddressInfo;
    const { name, phone, ...address } = info || {};
    handleAddressChange({
      ...{ value: { name, address, phone } },
      rfcValue: newValue,
      complete
    });
    if (newValue && !validateRFC(newValue)) {
      setShowRfcError(true);
    } else {
      setShowRfcError(false);
    }

    // 恢复光标位置
    setTimeout(() => {
      if (rfcInputRef.current) {
        rfcInputRef.current.setSelectionRange(
          newCursorPosition,
          newCursorPosition
        );
      }
    }, 0);
  };

  return (
    <div className={isMobile() ? styles.mobileContent : styles.content}>
      <div className="text-base font-semibold leading-6 mb-3">
        {t('modal.address.title', {
          defaultValue: 'Enter your address details'
        })}
      </div>
      <div className={styles.desc}>
        <span>
          {t('modal.address.description', {
            defaultValue:
              'Please enter your address to receive the product from the brand. Your address information is secure and protected, and it will only be used for the purpose of shipping the product to you.'
          })}
        </span>
        <div className="w-full mt-4 px-4 py-3 bg-[#FFFBEB] rounded-lg inline-flex justify-start items-start gap-8">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-1">
            <div className="flex justify-start items-top gap-2">
              <div>
                <TriangleExclamationIcon className="w-5 h-5" />
              </div>
              <div className="self-stretch inline-flex justify-start items-center">
                <div className="flex-1 justify-center">
                  <span className="text-[#57534D] text-sm font-normal leading-snug">
                    {t('modal.address.address_submission_reminder', {
                      defaultValue:
                        'After delivery, please complete the deal within the timeline to keep your creator rating in good standing. If a deal isn’t completed, you may be responsible for the product cost and shipping fees—unless the issue is caused by the brand or the product.'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${styles.stripeElements} ${isMobile() ? 'w-[calc(100%-4px)]' : ''}`}
      >
        <Elements
          stripe={stripePromise}
          options={{
            appearance: {
              variables: {
                fontFamily: "'Inter'",
                fontWeightNormal: '600'
              },
              rules: {
                '.Label': {
                  fontFamily: "'Inter'",
                  color: '#57534D',
                  fontSize: '12px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  opacity: '1'
                }
              }
            }
          }}
        >
          <AddressElement
            options={{
              mode: 'billing',
              fields: {
                phone: 'always'
              },
              validation: {
                phone: {
                  required: 'always'
                }
              },
              defaultValues: {
                name,
                address,
                phone
              }
            }}
            onChange={(event) => {
              handleAddressChange({
                ...event,
                rfcValue: rfcValue || address?.rfcValue || ''
              });
            }}
          />
        </Elements>
      </div>
      {addressInfo?.country === 'MX' && (
        <div className="flex flex-col gap-2 mt-4 w-full">
          <div className="justify-start">
            <span className="text-[#57534D] text-[12px] font-['Inter'] leading-5 font-[500]">
              {t('modal.address.rfc.label', {
                defaultValue: 'Tax Identification Number (RFC)'
              })}
            </span>
            <span className="text-[#DC2626] text-sm font-['Inter'] leading-5">
              *
            </span>
          </div>
          <div className="w-full">
            <div
              className={`h-10 max-h-10 min-h-10 px-3 py-2 bg-white rounded-md outline outline-1 outline-offset-[-1px] ${
                showRfcError ? 'outline-[#DC2626]' : 'outline-zinc-200'
              } flex justify-end items-center gap-16`}
            >
              <input
                ref={rfcInputRef}
                className="flex-1 w-full text-start text-[#292524] text-sm font-medium leading-5 bg-transparent border-none outline-none placeholder:text-[#A6A09B]"
                value={rfcValue}
                onChange={handleRfcChange}
                placeholder={t('modal.address.rfc.placeholder', {
                  defaultValue: 'e.g. GODE561231GR8'
                })}
                type="text"
              />
            </div>
            {showRfcError && (
              <div className="px-4 py-0.5 bg-[#FEE2E2] rounded flex justify-center items-start gap-1 mt-[10px]">
                <WarnRedIcon className="w-3 h-3 mt-1" />
                <div className="flex-1 justify-start text-[#DC2626] text-xs font-normal font-['Inter'] leading-5">
                  {t('modal.address.rfc.error_message', {
                    defaultValue:
                      'Invalid RFC format. Your RFC must contain 13 characters: 4 letters, 6 digits for your birth date (YYMMDD), and 3 alphanumeric characters.'
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModalAddress;
